import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

const fmtUSD = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(v);

const fmtSignedUSD = (v) => {
  const n = Number(v) || 0;
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${sign}${fmtUSD(Math.abs(n))}`;
};

export default function GrowthChart({ points }) {
  if (!points || points.length === 0) {
    return (
      <div className="h-64 rounded border border-neutral-800 bg-neutral-900 flex items-center justify-center">
        <p className="text-sm text-neutral-500">
          Growth history will appear after the first sync.
        </p>
      </div>
    );
  }
  return (
    <div className="h-64 rounded border border-neutral-800 bg-neutral-900 px-3 py-2">
      <ResponsiveContainer>
        <LineChart data={points} margin={{ top: 10, right: 16, bottom: 4, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
          <XAxis
            dataKey="date"
            tick={{ fill: '#737373', fontSize: 11 }}
            tickFormatter={(d) =>
              new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            }
          />
          <YAxis
            tick={{ fill: '#737373', fontSize: 11 }}
            tickFormatter={fmtUSD}
            width={70}
          />
          <Tooltip content={<GrowthTooltip points={points} />} />
          <Line
            type="monotone"
            dataKey="total_value"
            stroke="#818cf8"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Custom tooltip: hovered date + value, then D/D, W/W, M/M absolute
// changes computed against the prior snapshot ~1, ~7, ~30 calendar days
// back. Looks up by date rather than array index so sparse snapshot
// history doesn't misalign the comparison windows.
function GrowthTooltip({ active, payload, points }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const dateStr = row.date;
  const value = Number(row.total_value);

  const byDate = new Map((points || []).map((p) => [String(p.date), Number(p.total_value)]));

  const lookbackValue = (days) => {
    const target = new Date(dateStr);
    target.setUTCDate(target.getUTCDate() - days);
    // Walk back up to 5 days to tolerate missing snapshots (weekends, gaps).
    for (let i = 0; i <= 5; i++) {
      const key = target.toISOString().slice(0, 10);
      if (byDate.has(key)) return byDate.get(key);
      target.setUTCDate(target.getUTCDate() - 1);
    }
    return null;
  };

  const deltas = [
    { label: 'D/D', prev: lookbackValue(1) },
    { label: 'W/W', prev: lookbackValue(7) },
    { label: 'M/M', prev: lookbackValue(30) },
  ];

  const dateLabel = new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="rounded border border-neutral-700 bg-neutral-950 px-3 py-2 text-xs shadow-lg">
      <div className="text-neutral-400 mb-1">{dateLabel}</div>
      <div className="text-neutral-100 font-semibold tabular-nums">
        {fmtUSD(value)}
      </div>
      <div className="mt-2 pt-2 border-t border-neutral-800 space-y-0.5 text-[11px]">
        {deltas.map(({ label, prev }) => {
          if (prev == null) {
            return (
              <div key={label} className="flex items-center justify-between gap-3">
                <span className="text-neutral-500">{label}</span>
                <span className="text-neutral-600 tabular-nums">—</span>
              </div>
            );
          }
          const change = value - prev;
          const tone =
            change > 0 ? 'text-emerald-400' : change < 0 ? 'text-red-400' : 'text-neutral-400';
          return (
            <div key={label} className="flex items-center justify-between gap-3">
              <span className="text-neutral-500">{label}</span>
              <span className={`${tone} tabular-nums`}>{fmtSignedUSD(change)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
